import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar, ShieldCheck, Search, Boxes, Sparkles, ServerCog, Calculator, ArrowRight, Check, Loader2, Lock,
  Rocket, Wrench, HeartPulse, Globe, Mail, Link2, BarChart3, MapPin, Zap, Save, Inbox, Wand2, UserCog,
} from "lucide-react";
import ScoreGauge from "@/components/ScoreGauge";
import EmergentBuildPromo from "@/components/EmergentBuildPromo";
import InstallAppButton from "@/components/InstallAppButton";
import { MIGRATION_SERVICES } from "@/data/services";
import api, { API } from "@/lib/api";

const FEATURES = [
  { icon: Boxes, title: "Tech Stack Detection", desc: "Frameworks, CMS, CDN, and every third-party script — named and explained in plain English." },
  { icon: ShieldCheck, title: "Security & Privacy", desc: "TLS, security headers, CSP, insecure forms, trackers and compliance flags with concrete fixes." },
  { icon: Search, title: "Deep SEO Audit", desc: "Title, meta, H1, canonical, Open Graph, JSON-LD, robots & sitemap — scored 0–100." },
  { icon: Sparkles, title: "AI Build Brief", desc: "An engineer-grade action plan with prioritized fixes and recommended services." },
  { icon: ServerCog, title: "Backend Dependency Scan", desc: "Upload a manifest to flag real CVEs and end-of-life runtimes." },
  { icon: Calculator, title: "Migration Savings", desc: "See your potential annual savings by consolidating your stack onto one platform." },
];

// 5-path model — mirrors the Pricing page so the landing never drifts out of date.
const PATHS = [
  { key: "scan", icon: Radar, title: "Scan", price: "Free", per: "", tagline: "See where your site stands", cta: "Scan free",
    features: ["Overall health score", "Website & tech scan", "A preview of what's wrong"] },
  { key: "fix", icon: Wrench, title: "Fix", price: "$29", per: "DIY · $99 done-for-you", tagline: "Find it — then fix it",
    features: ["Full report + AI step-by-step fixes", "Or we implement the priority fixes", "Report unlocked + re-scan"] },
  { key: "move", icon: Rocket, title: "Move", price: "Quote", per: "", tagline: "Move your whole site to Emergent",
    features: ["Site, domain, DNS, SSL & email moved", "All your tools reconnected", "Zero downtime, fully tested"] },
  { key: "upgrade", icon: Sparkles, title: "Upgrade", price: "Quote", per: "", highlight: true, tagline: "Rebuild + move + business tools",
    features: ["Everything in Move", "Business email + Analytics/Search Console", "Hardening + a savings review"] },
  { key: "monitor", icon: HeartPulse, title: "Monitor", price: "$19.99", per: "/mo", tagline: "Always-on watch",
    features: ["Nightly re-scans + change alerts", "Unlimited reports across all sites", "Every issue unlocked"] },
];

const SERVICE_ICONS = {
  shield: ShieldCheck, globe: Globe, mail: Mail, rocket: Rocket, link: Link2, chart: BarChart3,
  pin: MapPin, lock: Lock, zap: Zap, save: Save, inbox: Inbox,
};

const SAMPLE = [
  { label: "Frontend", value: 85 }, { label: "Mobile", value: 80 }, { label: "Security", value: 60 },
  { label: "SEO", value: 75 }, { label: "Compliance", value: 87 }, { label: "Readiness", value: 74 },
];

const SCORE_LABELS = [
  ["frontend", "Frontend"], ["mobile", "Mobile"], ["security", "Security"],
  ["seo", "SEO"], ["compliance", "Compliance"], ["integration", "Readiness"],
];

export default function Landing() {
  const [domain, setDomain] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const runScan = async () => {
    const d = domain.trim();
    if (!d) { toast.error("Enter a domain to scan"); return; }
    setScanning(true); setResult(null);
    try {
      const res = await api.post("/public/scan", { domain: d });
      setResult(res.data);
      toast.success("Scan complete — here's a preview");
    } catch (e) {
      if (e?.response?.status === 429) toast.error("Free scan limit reached — sign up for unlimited scans.");
      else toast.error(e?.response?.data?.detail || "Could not scan that site");
    } finally { setScanning(false); }
  };

  const gauges = result?.scores
    ? SCORE_LABELS.filter(([k]) => result.scores[k] != null).map(([k, label]) => ({ label, value: result.scores[k] }))
    : SAMPLE;
  const pills = result?.detectedServices?.length
    ? result.detectedServices.map((s) => s.name)
    : ["React", "Cloudflare", "Stripe", "No CSP", "Google Fonts", "EOL jQuery"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 font-heading font-extrabold text-lg" data-testid="brand-logo">
            <img src="/brand-mark.png" alt="Link Website Pro" className="w-9 h-9 object-contain" />
            Link Website Pro
          </div>
          <div className="flex items-center gap-3">
            <a href="#move" className="text-sm text-zinc-300 hover:text-white transition-colors hidden sm:inline">Move to Emergent</a>
            <Link to="/why-emergent" data-testid="nav-why-emergent" className="text-sm text-zinc-300 hover:text-white transition-colors hidden sm:inline">Why Emergent</Link>
            <InstallAppButton variant="ghost" />
            <Link to="/login" data-testid="nav-signin" className="text-sm text-zinc-300 hover:text-white transition-colors">Sign in</Link>
            <Link to="/login" data-testid="nav-getstarted" className="btn-brand text-white text-sm rounded-lg px-4 py-2 font-medium">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#007AFF]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 text-[#4da3ff] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              <Radar size={15} /> Scan · Fix · Move · Monitor
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Scan your site. <span className="brand-gradient">Fix what's broken.</span>
            </h1>
            <p className="text-zinc-400 mt-5 text-lg max-w-xl">
              Get a free 30-second scan with plain-English, prioritized fixes for security, SEO,
              performance, mobile and more.
              <span className="text-zinc-200"> Fix them yourself, or get expert help whenever you want it.</span>
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-1.5 text-zinc-300"><UserCog size={15} className="text-[#4da3ff]" /> Fix it yourself</span>
              <span className="inline-flex items-center gap-1.5 text-zinc-300"><Wand2 size={15} className="text-[#9b5cff]" /> Or get expert help</span>
            </div>
            <Link to="/hipaa-check" data-testid="hero-hipaa-link"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#30D158] hover:text-[#2fd158] transition-colors">
              🩺 Run a healthcare practice? Try the free HIPAA website check →
            </Link>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="instant-scan-form">
              <input
                data-testid="instant-scan-input"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runScan()}
                placeholder="yourwebsite.com"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="url"
                className="flex-1 bg-black border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 font-mono text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] outline-none"
              />
              <button data-testid="instant-scan-btn" onClick={runScan} disabled={scanning} className="btn-brand disabled:opacity-50 text-white rounded-lg px-6 py-3 font-semibold flex items-center justify-center gap-2 shadow-[0_0_22px_rgba(155,92,255,0.3)] whitespace-nowrap">
                {scanning ? <><Loader2 size={18} className="animate-spin" /> Scanning…</> : <>Scan free <ArrowRight size={18} /></>}
              </button>
            </div>
            <p className="text-xs text-zinc-600 mt-4">Free instant scan · no account needed · <a href="#move" className="underline hover:text-zinc-400">explore your options</a>. Scan sites you own or are authorized to test.</p>
          </div>

          {/* Sample / live report card */}
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 relative" data-testid="sample-report">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#007AFF]/10 to-[#9b5cff]/10 pointer-events-none" />
            <div className="flex items-center justify-between mb-4 relative">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">{result ? "Your scan" : "Sample report"}</span>
              <span className="text-[10px] font-mono text-white/40 border border-white/10 rounded px-1.5 py-0.5 truncate max-w-[160px]">{result ? result.domain : "example.com"}</span>
            </div>
            <div className="grid grid-cols-3 gap-4 relative">
              {gauges.map((s) => <ScoreGauge key={s.label} value={s.value} size={72} stroke={7} label={s.label} />)}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-5 relative">
              {pills.map((t) => (
                <span key={t} className="text-[10px] text-zinc-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">{t}</span>
              ))}
            </div>
            {result && (
              <div className="mt-5 pt-4 border-t border-white/10 relative" data-testid="scan-gated-cta">
                <p className="text-sm text-white">
                  <span className="brand-gradient font-bold">{result.issuesFound}</span> issues found on {result.domain}.
                </p>
                <p className="text-zinc-400 text-xs mt-1">Get the full breakdown, prioritized fixes &amp; an AI build brief.</p>
                <Link to="/login" data-testid="unlock-report-cta" className="mt-3 btn-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                  <Lock size={14} /> Unlock full report — free sign-up
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <h2 className="font-heading text-3xl font-bold tracking-tight">Everything you need to know about your site</h2>
        <p className="text-zinc-400 mt-2">One scan surfaces what usually takes hours across a dozen tools — in plain English.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-white/25 hover:-translate-y-1 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                  <Icon size={19} className="text-[#4da3ff]" />
                </div>
                <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                <p className="text-zinc-400 text-sm mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Move to Emergent — DIY vs done-for-you */}
      <section id="move" className="max-w-7xl mx-auto px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#9b5cff] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            <Rocket size={15} /> Move to Emergent
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Get off your old host — your way</h2>
          <p className="text-zinc-400 mt-3">
            Stop juggling hosting, email, forms and a dozen tools that don't talk to each other. Move onto Emergent with a
            real backend — do it yourself, or hand us the keys and we'll handle the whole thing.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {/* DIY */}
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-7 flex flex-col" data-testid="path-diy">
            <div className="w-11 h-11 rounded-xl bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center mb-4">
              <UserCog size={22} className="text-[#4da3ff]" />
            </div>
            <h3 className="font-heading text-xl font-bold">Host it yourself on Emergent</h3>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              Prefer to stay hands-on? Build and self-host on Emergent. We'll show you exactly what to move and how —
              you keep full control of your site, domain and stack.
            </p>
            <ul className="space-y-2 my-5 flex-1">
              {["Follow the AI build brief step-by-step", "Point your domain & DNS to Emergent", "Keep the keys — no lock-in"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={15} className="text-[#30D158] mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/why-emergent" data-testid="diy-cta" className="block text-center rounded-lg px-4 py-2.5 font-medium text-sm border border-white/15 hover:border-white/30 text-white transition-colors">
              See how it works
            </Link>
          </div>

          {/* Done-for-you */}
          <div className="rounded-2xl border border-[#9b5cff]/40 bg-gradient-to-b from-[#9b5cff]/10 to-transparent p-7 flex flex-col relative" data-testid="path-dfy">
            <span className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#007AFF] to-[#9b5cff] text-white rounded-full px-3 py-1">Most popular</span>
            <div className="w-11 h-11 rounded-xl bg-[#9b5cff]/15 border border-[#9b5cff]/30 flex items-center justify-center mb-4">
              <Wand2 size={22} className="text-[#c9a6ff]" />
            </div>
            <h3 className="font-heading text-xl font-bold">We do it all for you</h3>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              Hand us your current host and we'll migrate everything with zero downtime — then keep it monitored and
              secured. You don't touch a thing.
            </p>
            <ul className="space-y-2 my-5 flex-1">
              {["We move your site, domain, DNS & SSL", "Business email & all tools reconnected", "Tested, hardened & monitored after launch"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={15} className="text-[#30D158] mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/login" data-testid="dfy-cta" className="block text-center rounded-lg px-4 py-2.5 font-medium text-sm btn-brand text-white">
              Get a free migration quote
            </Link>
          </div>
        </div>

        {/* What we move for you */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#0f0f0f] p-7" data-testid="migration-handles">
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-5">What we move &amp; handle for you</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {MIGRATION_SERVICES.map((s) => {
              const Icon = SERVICE_ICONS[s.icon] || Check;
              return (
                <div key={s.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-[#4da3ff]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{s.title}</p>
                    <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Build on Emergent promo */}
      <section className="max-w-7xl mx-auto px-5 pb-4">
        <EmergentBuildPromo />
      </section>

      {/* Pricing — full 5-path model */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 py-16">
        <h2 className="font-heading text-3xl font-bold tracking-tight text-center">Simple, honest pricing</h2>
        <p className="text-zinc-400 mt-2 text-center">Start with a free scan. Pay only for what you need — or let us move your whole site to Emergent.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-10">
          {PATHS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.key} data-testid={`plan-${p.key}`} className={`rounded-2xl p-5 relative flex flex-col ${p.highlight ? "border border-[#9b5cff]/40 bg-gradient-to-b from-[#9b5cff]/10 to-transparent" : "border border-white/10 bg-[#121212]"}`}>
                {p.highlight && <span className="absolute -top-3 left-5 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#007AFF] to-[#9b5cff] text-white rounded-full px-3 py-1">Best value</span>}
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={17} className="text-[#4da3ff]" />
                  <div className="font-heading font-semibold text-lg">{p.title}</div>
                </div>
                <div className="text-xs text-zinc-500 mb-3 min-h-[32px]">{p.tagline}</div>
                <div className="flex items-end gap-1 mb-4"><span className="text-3xl font-heading font-extrabold">{p.price}</span>{p.per && <span className="text-zinc-500 mb-1 text-xs">{p.per}</span>}</div>
                <ul className="space-y-2 mb-5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-300"><Check size={13} className="text-[#30D158] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Link to="/login" data-testid={`plan-cta-${p.key}`} className={`block text-center rounded-lg px-4 py-2.5 font-medium text-sm transition-colors ${p.highlight ? "btn-brand text-white" : "border border-white/15 hover:border-white/30 text-white"}`}>
                  {p.cta}
                </Link>
              </div>
            );
          })}
        </div>
        <p className="text-center text-zinc-500 text-sm mt-6">Move &amp; Upgrade are quoted to your site — <Link to="/login" className="underline hover:text-white">request a free quote</Link> and we'll scope it with you.</p>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="rounded-2xl p-10 text-center relative overflow-hidden border border-[#007AFF]/30 bg-gradient-to-r from-[#007AFF]/15 to-[#9b5cff]/15">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Scan free. Then keep it or let us move it.</h2>
          <p className="text-zinc-300 mt-3 max-w-xl mx-auto">Run your first scan free and get a plain-English fix list — then do it yourself, or hand the whole move to us.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Link to="/login" data-testid="footer-cta" className="btn-brand text-white rounded-lg px-7 py-3 font-semibold inline-flex items-center gap-2">Get started free <ArrowRight size={18} /></Link>
            <a href="#move" className="border border-white/20 hover:border-white/40 text-white rounded-lg px-7 py-3 font-semibold inline-flex items-center gap-2 transition-colors">See how we move you</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-zinc-600 text-xs">© 2026 Link‑Lite Data LLC · Link Website Pro</span>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            <a href={`${API}/capabilities.pdf`} target="_blank" rel="noreferrer" data-testid="capabilities-pdf-link" className="hover:text-white transition-colors font-medium text-[#4da3ff]">Capabilities (PDF)</a>
            <a href="/legal/terms.html" className="hover:text-white transition-colors">Terms</a>
            <a href="/legal/privacy.html" className="hover:text-white transition-colors">Privacy</a>
            <a href="/legal/acceptable-use.html" className="hover:text-white transition-colors">Acceptable Use</a>
            <a href="/legal/security.html" className="hover:text-white transition-colors">Security</a>
            <a href="/legal/" className="hover:text-white transition-colors font-medium text-zinc-400">Legal Center</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
